import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, ROLES, papelDeAcesso } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import { useDeleteModal } from '../hooks/useDeleteModal';

const IDEB_YEARS = [2005, 2007, 2009, 2011, 2013, 2015, 2017, 2019, 2021, 2023];

function IdebMunicipal() {
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // Espelha RoleBasedPermission em `city-ideb-averages`:
  // - Admin / Coordenador: tudo.
  // - Referência Técnica: cria e edita, não exclui.
  // - Supervisor / Diretor: edita e exclui, não cria.
  // - Gestor e Secretário: só leitura (o Secretário passa por Coordenador na
  //   rota, mas `user.role` aqui é o papel real, então ele fica de fora).
  const papel = papelDeAcesso(user?.role);
  const canCreate = [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA].includes(papel);
  // O Diretor(a) passa por Supervisor em `papel`, mas só altera a situação
  // dos alunos (api/authentication.py): aqui, só visualiza.
  const ehDiretor = user?.role === ROLES.DIRETOR;
  const canEdit = !ehDiretor && [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.SUPERVISOR].includes(papel);
  const canDelete = !ehDiretor && [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR].includes(papel);

  // Memoiza a cidade do usuário para manter a mesma referência entre renders.
  // Sem isso, getUserCity() retorna um objeto novo a cada render, o que torna
  // fetchEscolaData (useCallback) instável e dispara um loop de refetch — causa
  // do "piscar" da tela de carregamento na aba "Por Escola".
  const userCity = useMemo(() => {
    if (!user?.city) return null;
    return { id: user.city, name: user.city_name || 'Cidade não identificada' };
  }, [user?.city, user?.city_name]);

  // Tab state
  const [activeTab, setActiveTab] = useState('municipal');

  // ============ MUNICIPAL TAB STATE ============
  const [registros, setRegistros] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingMunicipal, setLoadingMunicipal] = useState(true);
  const [errorMunicipal, setErrorMunicipal] = useState(null);
  const [filterCity, setFilterCity] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    id_city: '',
    fiscal_year: '',
    education_stage: 'AI',
    ideb_score: '',
    state_average: '',
    national_average: '',
    ideb_target: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { isDeleteModalOpen, itemToDelete, openDeleteModal, closeDeleteModal } = useDeleteModal();

  // ============ ESCOLA TAB STATE ============
  const [escolas, setEscolas] = useState([]);
  const [loadingEscola, setLoadingEscola] = useState(true);
  const [errorEscola, setErrorEscola] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRede, setFilterRede] = useState('');
  const [selectedEscola, setSelectedEscola] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  // ============ MUNICIPAL LOGIC ============
  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await fetchAllPaginated('/cities/');
        setCities(data);
      } catch (err) {
        console.error('Erro ao buscar cidades:', err);
      }
    };
    loadCities();
  }, []);

  const fetchMunicipalData = useCallback(async () => {
    setLoadingMunicipal(true);
    setErrorMunicipal(null);
    try {
      const params = {};
      if (filterCity) params.id_city = filterCity;
      if (filterYear) params.fiscal_year = filterYear;
      if (filterStage) params.education_stage = filterStage;
      const data = await fetchAllPaginated('/city-ideb-averages/', params);
      setRegistros(data);
    } catch (err) {
      console.error('Erro ao buscar dados IDEB:', err);
      setErrorMunicipal('Erro ao carregar dados do IDEB. Tente novamente.');
    } finally {
      setLoadingMunicipal(false);
    }
  }, [filterCity, filterYear, filterStage]);

  useEffect(() => {
    fetchMunicipalData();
  }, [fetchMunicipalData]);

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      id_city: '',
      fiscal_year: '',
      education_stage: 'AI',
      ideb_score: '',
      state_average: '',
      national_average: '',
      ideb_target: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (registro) => {
    setEditing(registro);
    setFormData({
      id_city: registro.id_city || '',
      fiscal_year: registro.fiscal_year || '',
      education_stage: registro.education_stage || 'AI',
      ideb_score: registro.ideb_score || '',
      state_average: registro.state_average || '',
      national_average: registro.national_average || '',
      ideb_target: registro.ideb_target || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.id_city) errors.id_city = 'Selecione um município';
    if (!formData.fiscal_year) errors.fiscal_year = 'Informe o ano';
    if (!formData.education_stage) errors.education_stage = 'Selecione a etapa';
    if (!formData.ideb_score) errors.ideb_score = 'Informe a nota do IDEB';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        id_city: Number(formData.id_city),
        fiscal_year: Number(formData.fiscal_year),
        ideb_score: formData.ideb_score ? Number(formData.ideb_score) : null,
        state_average: formData.state_average ? Number(formData.state_average) : null,
        national_average: formData.national_average ? Number(formData.national_average) : null,
        ideb_target: formData.ideb_target ? Number(formData.ideb_target) : null,
      };
      if (editing) {
        await apiPut(`/city-ideb-averages/${editing.id}/`, dataToSend);
      } else {
        await apiPost('/city-ideb-averages/', dataToSend);
      }
      setShowModal(false);
      fetchMunicipalData();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setFormErrors({ submit: err.message || 'Erro ao salvar registro' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (registro) => {
    openDeleteModal(registro);
  };

  const handleDelete = async (registro) => {
    await apiDelete(`/city-ideb-averages/${registro.id}/`);
    fetchMunicipalData();
  };

  const clearFilters = () => {
    setFilterCity('');
    setFilterYear('');
    setFilterStage('');
  };

  const availableYears = [...new Set(registros.map(r => r.fiscal_year))].sort((a, b) => b - a);

  // ============ ESCOLA LOGIC ============
  const fetchEscolaData = useCallback(async () => {
    setLoadingEscola(true);
    setErrorEscola(null);
    try {
      const params = {};
      if (userCity) params.city_id = userCity.id;
      if (filterRede) params.rede = filterRede;
      const queryString = new URLSearchParams(params).toString();
      const url = `/ideb-escola/por-escola/${queryString ? '?' + queryString : ''}`;
      const data = await apiGet(url);
      setEscolas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao buscar dados IDEB por escola:', err);
      setErrorEscola('Erro ao carregar dados do IDEB por escola. Tente novamente.');
    } finally {
      setLoadingEscola(false);
    }
  }, [userCity, filterRede]);

  useEffect(() => {
    fetchEscolaData();
  }, [fetchEscolaData]);

  const getScoreColor = (score) => {
    if (!score && score !== 0) return darkMode ? 'text-slate-500' : 'text-slate-400';
    const num = Number(score);
    if (num >= 6.0) return 'text-[#13983D]';
    if (num >= 5.0) return 'text-[#0974BA]';
    if (num >= 4.0) return 'text-[#F9B047]';
    return 'text-[#E83A3C]';
  };

  const getScoreBg = (score) => {
    if (!score && score !== 0) return '';
    const num = Number(score);
    if (num >= 6.0) return darkMode ? 'bg-[#13983D]/10' : 'bg-[#13983D]/5';
    if (num >= 5.0) return darkMode ? 'bg-[#0974BA]/10' : 'bg-[#0974BA]/5';
    if (num >= 4.0) return darkMode ? 'bg-[#F9B047]/10' : 'bg-[#F9B047]/5';
    return darkMode ? 'bg-[#E83A3C]/10' : 'bg-[#E83A3C]/5';
  };

  const getLatestIdeb = (escola) => {
    for (let i = IDEB_YEARS.length - 1; i >= 0; i--) {
      const val = escola[`vl_observado_${IDEB_YEARS[i]}`];
      if (val !== null && val !== undefined) return { year: IDEB_YEARS[i], value: val };
    }
    return null;
  };

  const getIdebTrend = (escola) => {
    const values = IDEB_YEARS
      .map(y => ({ year: y, value: escola[`vl_observado_${y}`] }))
      .filter(v => v.value !== null && v.value !== undefined);
    if (values.length < 2) return null;
    const last = Number(values[values.length - 1].value);
    const prev = Number(values[values.length - 2].value);
    return last - prev;
  };

  const filteredEscolas = escolas.filter(e => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = (e.no_escola || '').toLowerCase().includes(term) ||
        (e.tb_school_name || '').toLowerCase().includes(term);
      if (!matchName) return false;
    }
    return true;
  });

  const escolaStats = {
    total: filteredEscolas.length,
    comIdeb: filteredEscolas.filter(e => getLatestIdeb(e)).length,
    mediaIdeb: (() => {
      const vals = filteredEscolas
        .map(e => getLatestIdeb(e))
        .filter(v => v)
        .map(v => Number(v.value));
      return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
    })(),
    tendenciaPositiva: filteredEscolas.filter(e => {
      const t = getIdebTrend(e);
      return t !== null && t > 0;
    }).length,
  };

  const redesDisponiveis = [...new Set(escolas.map(e => e.rede).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));

  // ============ TAB COMPONENTS ============

  const renderTabs = () => (
    <div className={`flex gap-1 p-1 rounded-xl mb-6 w-fit ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
      <button
        onClick={() => setActiveTab('municipal')}
        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
          activeTab === 'municipal'
            ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white shadow-md'
            : darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
        }`}
      >
        Municipal
      </button>
      <button
        onClick={() => setActiveTab('escola')}
        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
          activeTab === 'escola'
            ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white shadow-md'
            : darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
        }`}
      >
        Por Escola
      </button>
    </div>
  );

  // ============ MUNICIPAL TAB ============
  const renderMunicipalTab = () => {
    if (loadingMunicipal) {
      return (
        <LoadingSpinnerAdvanced
          darkMode={darkMode}
          message="Carregando IDEB Municipal..."
          subtitle="Buscando dados do IDEB dos municípios..."
          size="xl"
          animationType="spin-pulse"
        />
      );
    }

    if (errorMunicipal) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
            <p className="text-[#E83A3C]">{errorMunicipal}</p>
            <button
              onClick={fetchMunicipalData}
              className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
                : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Filters */}
        <div className={`rounded-xl p-4 mb-6 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SearchableSelect
              value={filterCity}
              onChange={setFilterCity}
              options={cities.map(c => ({ value: c.id, label: `${c.city} - ${c.state}` }))}
              emptyOptionLabel="Todos os municípios"
              darkMode={darkMode}
            />
            <SearchableSelect
              value={filterYear}
              onChange={setFilterYear}
              options={availableYears}
              emptyOptionLabel="Todos os anos"
              darkMode={darkMode}
            />
            <SearchableSelect
              value={filterStage}
              onChange={setFilterStage}
              options={[
                { value: 'AI', label: 'Anos Iniciais' },
                { value: 'AF', label: 'Anos Finais' },
              ]}
              emptyOptionLabel="Todas as etapas"
              darkMode={darkMode}
            />
          </div>

          {(filterCity || filterYear || filterStage) && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-[#0974BA] hover:text-[#0974BA]"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {registros.length}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Registros</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {[...new Set(registros.map(r => r.city_name || `Cidade ${r.id_city}`))].length}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Municípios</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {registros.filter(r => r.education_stage === 'AI').length}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Anos Iniciais</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {registros.filter(r => r.education_stage === 'AF').length}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Anos Finais</div>
          </div>
        </div>

        {/* Table */}
        {registros.length === 0 ? (
          <div className={`rounded-xl p-12 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-5xl mb-4">📊</div>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Nenhum registro encontrado
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {(filterCity || filterYear || filterStage)
                ? 'Nenhum registro corresponde aos filtros aplicados.'
                : canCreate
                  ? 'Adicione as notas do IDEB dos municípios clicando em "+ Novo Registro".'
                  : 'Ainda não há notas do IDEB cadastradas.'}
            </p>
          </div>
        ) : (
          <div className={`rounded-xl overflow-hidden ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                    <th className={`text-left px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Município</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Ano</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Etapa</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Nota IDEB</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Meta</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Média Estadual</th>
                    <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Média Nacional</th>
                    {(canEdit || canDelete) && (
                      <th className={`text-center px-4 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                  {registros.map((registro) => (
                    <tr key={registro.id} className={`transition-colors ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                      <td className={`px-4 py-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {registro.city_name}
                        {registro.state && <span className={`ml-1 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>- {registro.state}</span>}
                      </td>
                      <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {registro.fiscal_year}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          registro.education_stage === 'AI'
                            ? darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'
                            : darkMode ? 'bg-[#2E4974]/30 text-[#7BA3D4]' : 'bg-[#2E4974]/10 text-[#2E4974]'
                        }`}>
                          {registro.education_stage_display || (registro.education_stage === 'AI' ? 'Anos Iniciais' : 'Anos Finais')}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-center font-bold text-lg ${getScoreColor(registro.ideb_score)}`}>
                        {registro.ideb_score || '-'}
                      </td>
                      <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {registro.ideb_target || '-'}
                      </td>
                      <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {registro.state_average || '-'}
                      </td>
                      <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {registro.national_average || '-'}
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canEdit && (
                              <button
                                onClick={() => handleEdit(registro)}
                                className={`p-1.5 rounded-lg transition-colors ${darkMode
                                  ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                                  : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                                }`}
                                title="Editar"
                              >
                                ✏️
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteClick(registro)}
                                className={`p-1.5 rounded-lg transition-colors ${darkMode
                                  ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                                  : 'hover:bg-[#E83A3C]/5 text-slate-500 hover:text-[#E83A3C]'
                                }`}
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  // ============ ESCOLA TAB ============
  const renderEscolaTab = () => {
    if (loadingEscola) {
      return (
        <LoadingSpinnerAdvanced
          darkMode={darkMode}
          message="Carregando IDEB por Escola..."
          subtitle="Buscando dados historicos do IDEB das escolas..."
          size="xl"
          animationType="spin-pulse"
        />
      );
    }

    if (errorEscola) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
            <p className="text-[#E83A3C]">{errorEscola}</p>
            <button
              onClick={fetchEscolaData}
              className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
                ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
                : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Filtros */}
        <div className={`rounded-xl p-4 mb-6 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome da escola..."
              className={`px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder:text-slate-500'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder:text-slate-400'
              } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />
            <SearchableSelect
              value={filterRede}
              onChange={setFilterRede}
              options={redesDisponiveis}
              emptyOptionLabel="Todas as redes"
              darkMode={darkMode}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-[#0974BA] text-white'
                    : darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Tabela
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-[#0974BA] text-white'
                    : darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Cards
              </button>
            </div>
          </div>
        </div>

        {/* Estatisticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {escolaStats.total}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Escolas Encontradas</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {escolaStats.comIdeb}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Com Nota IDEB</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className={`text-2xl font-bold ${getScoreColor(escolaStats.mediaIdeb)}`}>
              {escolaStats.mediaIdeb || 'N/D'}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Media IDEB</div>
          </div>
          <div className={`rounded-xl p-4 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-2xl font-bold text-[#13983D]">
              {escolaStats.tendenciaPositiva}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tendencia Positiva</div>
          </div>
        </div>

        {/* Conteudo */}
        {filteredEscolas.length === 0 ? (
          <div className={`rounded-xl p-12 text-center ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="text-5xl mb-4">📊</div>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Nenhuma escola encontrada
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {searchTerm || filterRede
                ? 'Nenhuma escola corresponde aos filtros aplicados.'
                : 'Verifique se as escolas possuem o código INEP (inep_code) ou código IDEB preenchido para fazer o match com os dados do INEP.'}
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <div className={`rounded-xl overflow-hidden ${darkMode
            ? 'bg-slate-800/50 border border-slate-700/10'
            : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                    <th className={`text-left px-4 py-3 font-semibold sticky left-0 z-10 ${darkMode ? 'text-slate-300 bg-slate-700/50' : 'text-slate-600 bg-slate-50'}`}>
                      Escola
                    </th>
                    <th className={`text-center px-3 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Rede</th>
                    {IDEB_YEARS.map(year => (
                      <th key={year} className={`text-center px-3 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {year}
                      </th>
                    ))}
                    <th className={`text-center px-3 py-3 font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Tendencia</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                  {filteredEscolas.map((escola) => {
                    const trend = getIdebTrend(escola);
                    return (
                      <tr
                        key={escola.id}
                        className={`transition-colors cursor-pointer ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                        onClick={() => setSelectedEscola(escola)}
                      >
                        <td className={`px-4 py-3 font-medium sticky left-0 z-10 max-w-[250px] ${darkMode ? 'text-slate-200 bg-slate-800/90' : 'text-slate-700 bg-white/90'}`}>
                          <div className="truncate" title={escola.tb_school_name || escola.no_escola}>
                            {escola.tb_school_name || escola.no_escola}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            INEP: {escola.id_escola}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            escola.rede === 'Municipal'
                              ? darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'
                              : escola.rede === 'Estadual'
                                ? darkMode ? 'bg-[#2E4974]/30 text-[#7BA3D4]' : 'bg-[#2E4974]/10 text-[#2E4974]'
                                : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {escola.rede}
                          </span>
                        </td>
                        {IDEB_YEARS.map(year => {
                          const val = escola[`vl_observado_${year}`];
                          return (
                            <td key={year} className={`px-3 py-3 text-center font-semibold ${getScoreColor(val)} ${getScoreBg(val)} rounded`}>
                              {val !== null && val !== undefined ? Number(val).toFixed(1) : '-'}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center">
                          {trend !== null ? (
                            <span className={`font-semibold ${trend > 0 ? 'text-[#13983D]' : trend < 0 ? 'text-[#E83A3C]' : darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {trend > 0 ? '+' : ''}{trend.toFixed(1)}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEscolas.map((escola) => {
              const latest = getLatestIdeb(escola);
              const trend = getIdebTrend(escola);
              return (
                <div
                  key={escola.id}
                  onClick={() => setSelectedEscola(escola)}
                  className={`rounded-xl p-5 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg ${darkMode
                    ? 'bg-slate-800/50 border border-slate-700/10 hover:border-[#0974BA]/30'
                    : 'bg-white/80 border border-slate-200 shadow-sm hover:border-[#0974BA]/30'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold truncate ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {escola.tb_school_name || escola.no_escola}
                      </h3>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        INEP: {escola.id_escola} | {escola.rede}
                      </p>
                    </div>
                    {latest && (
                      <div className="text-right ml-3">
                        <div className={`text-2xl font-bold ${getScoreColor(latest.value)}`}>
                          {Number(latest.value).toFixed(1)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          IDEB {latest.year}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mini historico */}
                  <div className="flex items-end gap-1 h-12 mt-2">
                    {IDEB_YEARS.map(year => {
                      const val = escola[`vl_observado_${year}`];
                      if (val === null || val === undefined) return (
                        <div key={year} className="flex-1 flex flex-col items-center">
                          <div className={`w-full rounded-t ${darkMode ? 'bg-slate-700/30' : 'bg-slate-100'}`} style={{ height: '4px' }} />
                        </div>
                      );
                      const height = Math.max(8, (Number(val) / 10) * 48);
                      return (
                        <div key={year} className="flex-1 flex flex-col items-center justify-end" title={`${year}: ${val}`}>
                          <div
                            className={`w-full rounded-t transition-all ${
                              Number(val) >= 6 ? 'bg-[#13983D]' :
                              Number(val) >= 5 ? 'bg-[#0974BA]' :
                              Number(val) >= 4 ? 'bg-[#F9B047]' : 'bg-[#E83A3C]'
                            }`}
                            style={{ height: `${height}px` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>2005</span>
                    <span className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>2023</span>
                  </div>

                  {trend !== null && (
                    <div className={`mt-2 text-xs font-medium ${trend > 0 ? 'text-[#13983D]' : trend < 0 ? 'text-[#E83A3C]' : darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {trend > 0 ? 'Tendencia positiva' : trend < 0 ? 'Tendencia negativa' : 'Estavel'} ({trend > 0 ? '+' : ''}{trend.toFixed(1)})
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // ============ RENDER ============
  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
          }`}>
            IDEB
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            {activeTab === 'municipal'
              ? 'Gerencie as notas do IDEB por município e etapa de ensino'
              : 'Histórico de notas do IDEB (Anos Finais) das escolas cadastradas no sistema'}
            {userCity && <span className="text-sideb-blue"> - {userCity.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RefreshButton onClick={activeTab === 'municipal' ? fetchMunicipalData : fetchEscolaData} />
          {activeTab === 'municipal' && canCreate && (
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Novo Registro
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      {renderTabs()}

      {/* Tab Content */}
      {activeTab === 'municipal' ? renderMunicipalTab() : renderEscolaTab()}

      {/* Create/Edit Modal (Municipal) */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
          }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {editing ? 'Editar Registro IDEB' : 'Novo Registro IDEB'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Município *
                </label>
                <SearchableSelect
                  value={formData.id_city}
                  onChange={(v) => setFormData({ ...formData, id_city: v })}
                  options={cities.map(c => ({ value: c.id, label: `${c.city} - ${c.state}` }))}
                  placeholder="Selecione o município"
                  required
                  darkMode={darkMode}
                  className={`w-full ${formErrors.id_city ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                />
                {formErrors.id_city && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.id_city}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Ano *
                  </label>
                  <SearchableSelect
                    value={formData.fiscal_year}
                    onChange={(v) => setFormData({ ...formData, fiscal_year: v })}
                    options={['2017', '2019', '2021', '2023', '2025']}
                    placeholder="Selecione"
                    required
                    darkMode={darkMode}
                    className={`w-full ${formErrors.fiscal_year ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                  />
                  {formErrors.fiscal_year && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.fiscal_year}</p>}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Etapa de Ensino *
                  </label>
                  <SearchableSelect
                    value={formData.education_stage}
                    onChange={(v) => setFormData({ ...formData, education_stage: v })}
                    options={[
                      { value: 'AI', label: 'Anos Iniciais (1 ao 5 ano)' },
                      { value: 'AF', label: 'Anos Finais (6 ao 9 ano)' },
                    ]}
                    placeholder="Selecione"
                    required
                    darkMode={darkMode}
                    className={`w-full ${formErrors.education_stage ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                  />
                  {formErrors.education_stage && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.education_stage}</p>}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nota IDEB do Município *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={formData.ideb_score}
                  onChange={(e) => setFormData({ ...formData, ideb_score: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.ideb_score ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: 4.9"
                />
                {formErrors.ideb_score && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.ideb_score}</p>}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Meta IDEB Projetada
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={formData.ideb_target}
                  onChange={(e) => setFormData({ ...formData, ideb_target: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: 5.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Média Estadual
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={formData.state_average}
                    onChange={(e) => setFormData({ ...formData, state_average: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    placeholder="Ex: 5.7"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Média Nacional
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={formData.national_average}
                    onChange={(e) => setFormData({ ...formData, national_average: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    placeholder="Ex: 5.8"
                  />
                </div>
              </div>

              {formErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className="text-sm text-[#E83A3C]">{formErrors.submit}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                }`}
              >
                {saving ? 'Salvando...' : editing ? 'Salvar Alteracoes' : 'Criar Registro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal (Municipal) */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        item={itemToDelete}
        itemName={itemToDelete ? `${itemToDelete.city_name} - ${itemToDelete.fiscal_year} (${itemToDelete.education_stage === 'AI' ? 'Anos Iniciais' : 'Anos Finais'})` : ''}
        itemType="Registro IDEB"
        warningMessage="Este registro sera removido permanentemente. Digite sua senha para confirmar."
      />

      {/* Modal de Detalhes (Escola) */}
      {selectedEscola && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedEscola(null)}>
          <div
            className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 ${darkMode
              ? 'bg-slate-800 border border-slate-700'
              : 'bg-white shadow-xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {selectedEscola.tb_school_name || selectedEscola.no_escola}
                </h2>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  INEP: {selectedEscola.id_escola} | {selectedEscola.rede} | {selectedEscola.no_municipio}
                </p>
              </div>
              <button
                onClick={() => setSelectedEscola(null)}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-700 text-slate-400'
                  : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                X
              </button>
            </div>

            {/* IDEB Observado */}
            <div className="mb-6">
              <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                IDEB Observado
              </h3>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {IDEB_YEARS.map(year => {
                  const val = selectedEscola[`vl_observado_${year}`];
                  const proj = selectedEscola[`vl_projecao_${year}`];
                  return (
                    <div key={year} className={`rounded-lg p-2 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                      <div className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{year}</div>
                      <div className={`text-lg font-bold ${getScoreColor(val)}`}>
                        {val !== null && val !== undefined ? Number(val).toFixed(1) : '-'}
                      </div>
                      {proj !== null && proj !== undefined && (
                        <div className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Meta: {Number(proj).toFixed(1)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notas SAEB */}
            <div className="mb-6">
              <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Notas SAEB (Matemática e Português)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}>
                      <th className={`text-left px-3 py-2 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Disciplina</th>
                      {IDEB_YEARS.map(y => (
                        <th key={y} className={`text-center px-2 py-2 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{y}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                    <tr>
                      <td className={`px-3 py-2 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Matemática</td>
                      {IDEB_YEARS.map(y => {
                        const val = selectedEscola[`vl_nota_matematica_${y}`];
                        return (
                          <td key={y} className={`px-2 py-2 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {val !== null && val !== undefined ? Number(val).toFixed(1) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className={`px-3 py-2 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Português</td>
                      {IDEB_YEARS.map(y => {
                        const val = selectedEscola[`vl_nota_portugues_${y}`];
                        return (
                          <td key={y} className={`px-2 py-2 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {val !== null && val !== undefined ? Number(val).toFixed(1) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Taxa de Aprovacao */}
            <div>
              <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Taxa de Aprovacao - Media Geral (%)
              </h3>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {IDEB_YEARS.map(year => {
                  const val = selectedEscola[`vl_aprovacao_${year}_si4`];
                  return (
                    <div key={year} className={`rounded-lg p-2 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                      <div className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{year}</div>
                      <div className={`text-sm font-semibold ${
                        val !== null && val !== undefined
                          ? Number(val) >= 90 ? 'text-[#13983D]'
                            : Number(val) >= 80 ? 'text-[#0974BA]'
                            : Number(val) >= 70 ? 'text-[#F9B047]'
                            : 'text-[#E83A3C]'
                          : darkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}>
                        {val !== null && val !== undefined ? `${Number(val).toFixed(1)}%` : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IdebMunicipal;
